import { NgIf, NgFor, AsyncPipe, LowerCasePipe, DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemIcon,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
  MatDivider,
} from '@angular/material/list';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent, ExtendedNotificationComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { WrapperSiteSidenavActions } from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';

import { Movement } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { MovementItemDialogComponent } from '../movement-item-dialog/movement-item-dialog.component';

@Component({
  selector: 'app-movement-overview',
  templateUrl: 'movement-overview.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    MatNavList,
    NgFor,
    MatListItem,
    MatIcon,
    MatListItemIcon,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatIconButton,
    MatListItemMeta,
    MatDivider,
    ExtendedNotificationComponent,
    AsyncPipe,
    LowerCasePipe,
    DatePipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class MovementOverviewComponent {
  readonly cycle$ = this.store.select(getSelectedCycle);
  readonly isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  constructor(
    private readonly store: Store<RootState>,
    private readonly router: Router,
    @Inject(BreakpointServiceInjector)
    private readonly breakpoint: BreakpointService,
    private readonly dialog: DialogService,
    private readonly localeService: LocaleService,
  ) {
    this.store.dispatch(cycleActions.loadPropagationCompaniesRequest());
  }

  add() {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      this.router.navigate(['/cycle', cycle.id, 'movements', 'add']);
      this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
        if (isMobile) {
          this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
        }
      });
    });
  }

  addDialog() {
    this.store.dispatch(
      cycleActions.setSelectedMovementId({
        id: undefined,
      }),
    );
    this.cycle$
      .pipe(
        skipWhile((c) => !c?.id),
        take(1),
      )
      .subscribe((cycle) => {
        this.dialog
          .form(MovementItemDialogComponent, {
            confirmButton: true,
            confirmButtonText: this.localeService.translate('add'),
            pushHistoryState: false,
            movementId: null,
          })
          .subscribe((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              const nextMovementId =
                cycle.movements && cycle.movements.length > 0
                  ? Math.max(...cycle.movements.map((d) => d.id)) + 1
                  : 1;
              this.store.dispatch(
                cycleActions.addMovement({
                  cycleId: cycle.id,
                  movement: { ...result.result.movement, id: nextMovementId },
                }),
              );
            }
          });
      });
  }

  openMovementEdit(movementId: number, isLast: boolean) {
    this.store.dispatch(
      cycleActions.setSelectedMovementId({
        id: movementId,
      }),
    );
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      this.dialog
        .form(MovementItemDialogComponent, {
          confirmButton: isLast,
          confirmButtonText: this.localeService.translate('save'),
          pushHistoryState: false,
          movementId,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(
              cycleActions.editMovement({ cycleId: cycle.id, movement: result.result.movement }),
            );
          }
          this.store.dispatch(
            cycleActions.setSelectedMovementId({
              id: undefined,
            }),
          );
        });
    });
  }

  delete(event: Event, movement: Movement) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      if (movement && movement.id) {
        this.store.dispatch(
          cycleActions.deleteMovementWithConfirmation({ cycleId: cycle.id, id: movement.id }),
        );
      }
    });
  }

  sort(movements: Array<Movement>): Array<Movement> {
    return movements.slice().sort((a, b) => (a.movementDate < b.movementDate ? -1 : 0));
  }
}
